import { z } from 'zod';
import { expenseClassificationSchema } from './bookkeeping.js';
import { isoDateTimeSchema, pageQuerySchema, uuidSchema } from './common.js';
/**
 * The supplier contract.
 *
 * Every engine since v0.3 revolves around a supplier, so this is the screen a
 * bookkeeper lives in. It carries what the system has *learned* about a
 * supplier next to what somebody *told* it, and keeps the two visibly apart:
 * `recordCount` and `meanAmountMinor` are counted from the ledger, while
 * `defaultCategoryId` is a decision a person made.
 */
/**
 * A Canadian Business Number as printed, optionally with its program account.
 *
 * Shape only. Whether the check digit is right is `taxNumberWellFormed`, and
 * whether the number is actually registered is `taxNumberVerifiedAt`, which
 * only a person can set.
 */
export const businessNumberSchema = z
    .string()
    .trim()
    .regex(/^[0-9]{9}([A-Z]{2}[0-9]{4})?$/, 'Nine digits, optionally followed by RT0001.');
export const vendorSummarySchema = z.object({
    id: uuidSchema,
    displayName: z.string(),
    legalName: z.string().nullable(),
    mailingAddress: z.string().nullable(),
    defaultCategoryId: uuidSchema.nullable(),
    defaultCategoryName: z.string().nullable(),
    defaultClassification: expenseClassificationSchema.nullable(),
    requiresT5018: z.boolean(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    website: z.string().nullable(),
    notes: z.string().nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
    /* ---- learned, not entered ------------------------------------------- */
    aliasCount: z.number().int(),
    recordCount: z.number().int(),
    firstRecordedOn: z.string().nullable(),
    lastRecordedOn: z.string().nullable(),
    totalAmountMinor: z.string().nullable(),
    meanAmountMinor: z.string().nullable(),
    meanTaxRateBp: z.number().int().nullable(),
    /* ---- registration ---------------------------------------------------- */
    gstHstNumber: z.string().nullable(),
    chargesGstHst: z.boolean().nullable(),
    taxNumberVerifiedAt: isoDateTimeSchema.nullable(),
    /** Luhn check. Null when there is no number to check. */
    taxNumberWellFormed: z.boolean().nullable(),
    /** What is riding on that number being right. */
    itcClaimedMinor: z.string(),
});
export const vendorAliasSchema = z.object({
    id: uuidSchema,
    rawAlias: z.string(),
    normalizedAlias: z.string(),
    humanConfirmed: z.boolean(),
    source: z.string(),
    matchCount: z.number().int(),
    lastMatchedAt: isoDateTimeSchema.nullable(),
});
export const vendorTaxInfoSchema = z.object({
    id: uuidSchema,
    gstHstNumber: z.string().nullable(),
    chargesGstHst: z.boolean(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    verifiedAt: isoDateTimeSchema.nullable(),
    notes: z.string().nullable(),
    wellFormed: z.boolean().nullable(),
});
export const vendorDetailSchema = vendorSummarySchema.extend({
    aliases: z.array(vendorAliasSchema),
    taxInformation: z.array(vendorTaxInfoSchema),
});
export const listVendorsQuerySchema = pageQuerySchema.extend({
    query: z.string().trim().min(1).max(120).optional(),
    includeArchived: z.coerce.boolean().optional(),
    /** The worklist: suppliers whose registration is missing or impossible. */
    needsAttention: z.coerce.boolean().optional(),
    /**
     * Ordering, done in SQL rather than in the browser. Sorting only the rows
     * that happen to have been fetched would put the right arrow on a column
     * and the wrong supplier at the top.
     */
    sort: z.enum(['name', 'spend', 'records', 'recent']).optional(),
});
export const createVendorRequestSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(200),
    legalName: z.string().trim().max(200).nullable().optional(),
    /** Printed on the T5018 slip for a subcontractor. */
    mailingAddress: z.string().trim().max(400).nullable().optional(),
    defaultCategoryId: uuidSchema.nullable().optional(),
    defaultClassification: expenseClassificationSchema.nullable().optional(),
    requiresT5018: z.boolean().optional(),
    email: z.email().nullable().optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    website: z.string().trim().max(300).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
});
export const updateVendorRequestSchema = createVendorRequestSchema.partial().extend({
    /** Archived, never deleted: transactions and learned rules point here. */
    archived: z.boolean().optional(),
});
export const addAliasRequestSchema = z.strictObject({
    rawAlias: z.string().trim().min(1).max(300),
});
export const setTaxInfoRequestSchema = z.strictObject({
    gstHstNumber: businessNumberSchema.nullable(),
    chargesGstHst: z.boolean(),
    effectiveFrom: z.string(),
    notes: z.string().trim().max(1000).optional(),
});
export const verifyTaxInfoRequestSchema = z.strictObject({
    /** Who checked it, and where. "Verified" alone is not a record. */
    note: z.string().trim().min(10).max(500),
});
export const mergeVendorsRequestSchema = z.strictObject({
    sourceId: uuidSchema,
    reason: z.string().trim().min(3).max(500),
});
/** What to say about a supplier's registration, in one place. */
export function registrationStatus(vendor) {
    if (vendor.chargesGstHst === false) {
        return { tone: 'success', label: 'Not registered, and does not charge HST' };
    }
    if (vendor.gstHstNumber === null) {
        return { tone: 'danger', label: 'No GST/HST number on file' };
    }
    if (vendor.taxNumberWellFormed === false) {
        // The check digit says this number cannot be right, whoever typed it.
        return { tone: 'danger', label: 'That GST/HST number cannot be correct' };
    }
    if (vendor.taxNumberVerifiedAt === null) {
        return { tone: 'warning', label: 'Not checked against the CRA registry' };
    }
    return { tone: 'success', label: 'Checked' };
}
//# sourceMappingURL=vendor.js.map